import React, { useEffect, useMemo, useState, useCallback } from "react";
import Button from "components/ui/Button";
import Select from "components/ui/Select";
import Icon from "components/AppIcon";
import { useLocationContext } from "context/LocationContext";
import { QUARTER_OPTIONS, YEAR_OPTIONS } from "./monitoringConstants";
import instance from "api/cmsapi";
import useAuth from "hooks/useAuth";
import { toast } from "sonner";
import KpiDataSourceButton from "./KpiDataSourceButton";

const ALL_DISTRICTS_OPTION = { label: "All Districts", value: "all" };

function padDatePart(value) {
  return String(value).padStart(2, "0");
}

function formatDateInputValue(date) {
  return `${date.getFullYear()}-${padDatePart(date.getMonth() + 1)}-${padDatePart(date.getDate())}`;
}

function formatTimeInputValue(date) {
  return `${padDatePart(date.getHours())}:${padDatePart(date.getMinutes())}`;
}

function createInitialMetadata(assessorName) {
  const now = new Date();
  return {
    leadAssessor: assessorName || "",
    assessmentDate: formatDateInputValue(now),
    startTime: formatTimeInputValue(now),
    endTime: "",
  };
}

function getKpiStatus(response) {
  const hasComment = Boolean(String(response?.comment || "").trim());
  const hasScore = response?.score !== "" && response?.score !== null && response?.score !== undefined;
  if (!hasComment && !hasScore) return "Not Started";
  if (hasComment && hasScore) return "Complete";
  return "In Progress";
}

function statusClass(status) {
  if (status === "Complete") return "bg-success/10 text-success";
  if (status === "In Progress") return "bg-[#16325a]/10 text-[#16325a]";
  return "bg-muted text-muted-foreground";
}

function formatDateArray(dateArray) {
  if (!dateArray || !Array.isArray(dateArray) || dateArray.length < 3) return null;
  const [year, month, day] = dateArray;
  return `${year}-${padDatePart(month)}-${padDatePart(day)}`;
}

// API Functions
const getKPIsByYearAndLocationType = async (year, locationType) => {
  const { data } = await instance.get(`kpi/year/${year}/location-type/${locationType}`);
  return data;
};

const getOhlgsKpas = async () => {
  const { data } = await instance.get("ohlgs-kpa");
  return data;
};

const getContractPerformanceAssessments = async (params) => {
  const { data } = await instance.get("contract-performance-assessment", { params });
  return data;
};

const saveContractPerformanceAssessment = async (payload) => {
  const response = await instance.post("contract-performance-assessment", payload);
  if (response?.status === 201) {
    toast.success("Save KPA successful");
  }
  return response?.data;
};

const getAssessments = async (params) => {
  const { data } = await instance.get("assessments", { params });
  return data;
};

const saveAssessmentLifecycle = async (payload) => {
  const { data } = await instance.post("assessments", payload);
  return data;
};

const getWriteUps = async () => {
  const { data } = await instance.get("write-ups");
  return data;
};

const saveWriteUp = async (payload) => {
  const { data } = await instance.post("write-ups", payload);
  return data;
};

const OHLGS_GENERAL_COMMENTS_SECTION = "OHLGS_GeneralComments";
const OHLGS_RECOMMENDATIONS_SECTION = "OHLGS_Recommendations";
const OHLGS_SCOPE_OF_ASSESSMENT_SECTION = "OHLGS_ScopeOfAssessment";
const OHLGS_GENERAL_OUTCOME_SECTION = "OHLGS_GeneralOutcome";

function getAssessmentKpiId(assessment) {
  const directId = Number(assessment?.kpiId);
  if (!Number.isNaN(directId) && directId > 0) return directId;

  const nestedId = Number(assessment?.kpi?.kpiId);
  if (!Number.isNaN(nestedId) && nestedId > 0) return nestedId;

  return null;
}

function getAssessmentYear(assessment) {
  const directYear = assessment?.year ?? assessment?.kpi?.year;
  if (directYear === undefined || directYear === null || directYear === "") return null;
  return String(directYear);
}

function normalizeLocationCode(value) {
  return String(value || "").trim();
}

function filterAssessmentsForScope(assessments, { locationCode, year, locationType }) {
  const normalizedLocationCode = normalizeLocationCode(locationCode);
  const normalizedYear = String(year || "");
  const normalizedLocationType = String(locationType || "").toUpperCase();

  return (Array.isArray(assessments) ? assessments : []).filter((assessment) => {
    const assessmentLocationCode = normalizeLocationCode(assessment?.locationCode);
    const assessmentYear = getAssessmentYear(assessment);
    const assessmentLocationType = String(assessment?.locationType || "").toUpperCase();

    const matchesLocationCode = normalizedLocationCode
      ? assessmentLocationCode === normalizedLocationCode
      : true;
    const matchesYear = normalizedYear ? assessmentYear === normalizedYear : true;
    const matchesLocationType = normalizedLocationType
      ? assessmentLocationType === normalizedLocationType
      : true;

    return matchesLocationCode && matchesYear && matchesLocationType;
  });
}

function getLifecycleTypeByLocationType(locationType) {
  if (String(locationType || "").toUpperCase() === "DISTRICT") {
    return "OHLGS-D";
  }
  return "OHLGS-R";
}

function writeUpTimestamp(value) {
  if (Array.isArray(value) && value.length >= 3) {
    const [year, month, day, hour = 0, minute = 0, second = 0] = value;
    return new Date(year, month - 1, day, hour, minute, second).getTime();
  }
  const parsed = Date.parse(value);
  return Number.isNaN(parsed) ? 0 : parsed;
}

function SelectionRequiredState({ requiredLevelLabel, requiredLevelName }) {
  return (
    <div className="main-content">
      <div className="card">
        <h2 className="mb-2 text-xl font-semibold text-foreground">{requiredLevelLabel} Selection Required</h2>
        <p className="text-muted-foreground">
          OHLGS Assessment is available at {requiredLevelName} level. Select a {requiredLevelName} from the sidebar, then open this module.
        </p>
      </div>
    </div>
  );
}

function AssessmentPeriodSelection({ selectedYear, selectedQuarter, onYearChange, onQuarterChange, onLoad, isLoading }) {
  return (
    <div className="main-content flex items-center justify-center p-6">
      <div className="w-full max-w-lg border border-slate-200 bg-white rounded-xl shadow-sm">
        <div className="bg-slate-50 border-b border-slate-100 px-8 py-10 text-center rounded-t-xl">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-white border border-slate-200 mb-4 shadow-sm">
            <Icon name="Calendar" className="h-6 w-6 text-slate-600" />
          </div>
          <h2 className="text-xl font-semibold text-slate-900">OHLGS Assessment Period</h2>
          <p className="text-sm text-slate-500 mt-2 max-w-[280px] mx-auto leading-relaxed">
            Select the specific fiscal year and quarter to load the OHLGS assessment records.
          </p>
        </div>
        <div className="p-8 space-y-6">
          <div className="grid grid-cols-1 gap-6">
            <div className="space-y-">
              <label className="text-[11px] font-bold uppercase tracking-wider text-slate-400">Assessment Year</label>
              <Select options={YEAR_OPTIONS} value={selectedYear} onChange={onYearChange} placeholder="e.g. 2026" className="w-full" />
            </div>

          </div>
          <Button type="button" variant="default" className="w-full h-12 bg-slate-900 hover:bg-slate-800 text-white transition-all shadow-md disabled:opacity-20" onClick={onLoad} disabled={!selectedYear  || isLoading}>
            {isLoading ? (
              <>
                <Icon name="Loader2" className="mr-2 h-4 w-4 animate-spin" />
                Fetching Assessment...
              </>
            ) : (
              <>
                <Icon name="Search" className="mr-2 h-4 w-4" />
                Load Assessment
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}

function KpiAssessmentItem({
  indicator,
  response,
  onScoreChange,
  onCommentChange,
  onKeyIssuesChange,
  onRecommendationChange,
  isReadOnly,
  reviewYear,
}) {
  const [isExpanded, setIsExpanded] = useState(true);
  const status = getKpiStatus(response);

  // Calculate KPI result: (Actual Rating / Max Score) × KPI Weight
  const calculateKpiResult = () => {
    const actualRating = Number(response?.score) || 0;
    const maxScore = Number(indicator?.maxScore) || 4;
    const kpiWeight = Number(indicator?.weight) || 1;
    
    if (actualRating === 0 || maxScore === 0) return 0;
    
    return (actualRating / maxScore) * kpiWeight;
  };

  const kpiResult = calculateKpiResult();

  return (
    <div className="ml-4 mt-4 first:mt-0">
      <div className="overflow-hidden rounded-xl border border-border bg-background">
        <div className="flex w-full items-start justify-between gap-3 p-4">
          <button
            type="button"
            onClick={() => setIsExpanded((current) => !current)}
            className="flex min-w-0 flex-1 items-start gap-3 text-left transition-colors hover:opacity-90"
          >
            <Icon name={isExpanded ? "ChevronDown" : "ChevronRight"} className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <span className="rounded bg-muted px-2 py-0.5 text-xs font-mono text-foreground">
                  {indicator.kpiName || `KPI ${indicator.kpiId}`}
                </span>
                <span className={`rounded-full px-2 py-0.5 text-xs ${statusClass(status)}`}>
                  {status}
                </span>
              </div>
              <p className="mt-1 text-sm font-medium text-foreground">
                {indicator.kpiName || "Untitled KPI"}
              </p>
            </div>
          </button>

          <div className="flex shrink-0 flex-col items-end gap-1 text-right">
            <p className="text-xs text-muted-foreground">Rate</p>
            <p className="text-sm font-semibold text-foreground">
              {response?.score !== "" && response?.score !== null && response?.score !== undefined ? response.score : "--"}
            </p>
            {response?.score !== "" && response?.score !== null && response?.score !== undefined && (
              <p className="text-xs text-blue-600 font-bold">
                Result: {kpiResult.toFixed(2)}
              </p>
            )}
            <KpiDataSourceButton
              kpiText={indicator.kpiName}
              scoringText={[indicator.scoringCriteria, indicator.kpiDescription, indicator.description, indicator.statement]
                .filter(Boolean)
                .join(" ")}
              year={reviewYear}
            />
          </div>
        </div>

        {isExpanded ? (
          <div className="space-y-4 border-t border-border p-4">
            <div className="rounded-lg bg-muted/15 p-3">
              <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                Scoring Guide
              </p>
              <div className="text-sm text-foreground whitespace-pre-wrap">
                {indicator.scoringCriteria || "No scoring criteria provided."}
              </div>
            </div>

            <div className="grid gap-4 xl:grid-cols-[220px_minmax(0,1fr)]">
              <label className="space-y-2">
                <span className="text-xs font-semibold uppercase tracking-[0.12em] text-muted-foreground">
                  Rate ({indicator.minScore || 0}-{indicator.maxScore || 4})
                </span>
                <select
                  value={response?.score ?? ""}
                  onChange={(event) => onScoreChange(indicator.kpiId, event.target.value)}
                  disabled={isReadOnly}
                  className="w-full rounded-xl border border-input bg-background px-3 py-2.5 text-sm text-foreground outline-none transition focus:border-[#16325a] focus:ring-2 focus:ring-[#16325a]/20"
                >
                  <option value="">Select</option>
                  {Array.from({ length: (indicator.maxScore || 4) + 1 }, (_, i) => i).map((score) => (
                    <option key={score} value={score}>
                      {score}
                    </option>
                  ))}
                </select>
              </label>

              <label className="space-y-2">
                <span className="text-xs font-semibold uppercase tracking-[0.12em] text-muted-foreground">
                  Justification / Comments / Observation
                </span>
                <textarea
                  rows={6}
                  value={response?.comment || ""}
                  onChange={(event) => onCommentChange(indicator.kpiId, event.target.value)}
                  disabled={isReadOnly}
                  placeholder="Capture the justification, comments, observation, and evidence for this KPI..."
                  className="w-full rounded-2xl border border-input bg-background px-3 py-3 text-sm leading-6 text-foreground outline-none transition focus:border-[#16325a] focus:ring-2 focus:ring-[#16325a]/20"
                />
              </label>
            </div>

            <div className="grid gap-4 xl:grid-cols-2">
              <label className="space-y-2">
                <span className="text-xs font-semibold uppercase tracking-[0.12em] text-muted-foreground">
                  Key Issues
                </span>
                <textarea
                  rows={4}
                  value={response?.keyIssues || ""}
                  onChange={(event) => onKeyIssuesChange(indicator.kpiId, event.target.value)}
                  disabled={isReadOnly}
                  placeholder="Capture key issues for this KPI..."
                  className="w-full rounded-2xl border border-input bg-background px-3 py-3 text-sm leading-6 text-foreground outline-none transition focus:border-[#16325a] focus:ring-2 focus:ring-[#16325a]/20"
                />
              </label>

              <label className="space-y-2">
                <span className="text-xs font-semibold uppercase tracking-[0.12em] text-muted-foreground">
                  Recommendation
                </span>
                <textarea
                  rows={4}
                  value={response?.recommendation || ""}
                  onChange={(event) => onRecommendationChange(indicator.kpiId, event.target.value)}
                  disabled={isReadOnly}
                  placeholder="Capture recommendations for this KPI..."
                  className="w-full rounded-2xl border border-input bg-background px-3 py-3 text-sm leading-6 text-foreground outline-none transition focus:border-[#16325a] focus:ring-2 focus:ring-[#16325a]/20"
                />
              </label>
            </div>

            <div className="grid grid-cols-2 gap-4 text-xs text-muted-foreground">
              <div>
                <span className="font-semibold">Weight:</span> {indicator.weight || 25}%
              </div>
              <div>
                <span className="font-semibold">Timeline:</span> {formatDateArray(indicator.expectedStartDate) || "N/A"} to {formatDateArray(indicator.expectedEndDate) || "N/A"}
              </div>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}

function KpaAssessmentCard({
  kpa,
  indicators,
  responses,
  onScoreChange,
  onCommentChange,
  onKeyIssuesChange,
  onRecommendationChange,
  onSave,
  saving,
  isReadOnly,
  reviewYear,
}) {
  // Calculate section summary
  const sectionSummary = indicators.reduce(
    (summary, indicator) => {
      const response = responses[indicator.kpiId] || {};
      const numericScore = Number(response.score);
      const hasScore = response.score !== "" && !Number.isNaN(numericScore);
      const isComplete = getKpiStatus(response) === "Complete";

      summary.totalItems += 1;
      summary.maxScore += indicator.maxScore || 4;

      if (hasScore) {
        summary.score += numericScore;
      }

      if (isComplete) {
        summary.completedItems += 1;
      }

      return summary;
    },
    { totalItems: 0, completedItems: 0, score: 0, maxScore: 0 }
  );

  // Calculate KPA results: sum of individual KPI results and final weighted result
  // KPA Score = (KPA Weight / 100) × ((Actual Rating / Max Score) × KPI Weight)
  const calculateKpaResults = () => {
    let kpiResultsSum = 0;
    
    indicators.forEach(indicator => {
      const response = responses[indicator.kpiId] || {};
      const actualRating = Number(response?.score) || 0;
      const maxScore = Number(indicator?.maxScore) || 4;
      const kpiWeight = Number(indicator?.weight) || 1;
      
      if (actualRating > 0 && maxScore > 0) {
        const kpiResult = (actualRating / maxScore) * kpiWeight;
        kpiResultsSum += kpiResult;
      }
    });
    
    const kpaWeight = Number(kpa?.weight) || 1;
    const finalWeightedResult = (kpaWeight / 100) * kpiResultsSum;
    
    return {
      summedKpiResults: kpiResultsSum,
      finalWeightedResult: finalWeightedResult
    };
  };

  const { summedKpiResults, finalWeightedResult } = calculateKpaResults();

  return (
    <section className="card overflow-hidden">
      <div className="border-b border-border p-5">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
          <div>
            <div className="flex flex-wrap items-center gap-2">
              <span className="rounded bg-muted px-2 py-0.5 text-xs font-mono text-foreground">
                {kpa.kpaName}
              </span>
            </div>
            <h3 className="mt-2 text-lg font-semibold text-foreground">{kpa.kpaName}</h3>
            <p className="mt-1 text-sm text-muted-foreground">{kpa.kpaDescription}</p>
          </div>

          <div className="grid grid-cols-2 gap-3 sm:grid-cols-5">
            <div className="rounded-xl border border-border bg-background px-4 py-3">
              <p className="text-[11px] uppercase tracking-[0.12em] text-muted-foreground">Score</p>
              <p className="mt-1 text-lg font-semibold text-foreground">
                {sectionSummary.score}
                <span className="ml-1 text-sm font-medium text-muted-foreground">/ {sectionSummary.maxScore}</span>
              </p>
            </div>
            <div className="rounded-xl border border-border bg-background px-4 py-3">
              <p className="text-[11px] uppercase tracking-[0.12em] text-muted-foreground">Completed</p>
              <p className="mt-1 text-lg font-semibold text-foreground">
                {sectionSummary.completedItems}
                <span className="ml-1 text-sm font-medium text-muted-foreground">/ {sectionSummary.totalItems}</span>
              </p>
            </div>
            <div className="rounded-xl border border-border bg-background px-4 py-3">
              <p className="text-[11px] uppercase tracking-[0.12em] text-muted-foreground">KPIs</p>
              <p className="mt-1 text-lg font-semibold text-foreground">{indicators.length}</p>
            </div>
            <div className="rounded-xl border border-border bg-green-50 px-4 py-3">
              <p className="text-[11px] uppercase tracking-[0.12em] text-green-600">Summed KPIs</p>
              <p className="mt-1 text-lg font-bold text-green-700">
                {summedKpiResults.toFixed(2)}
              </p>
            </div>
            <div className="rounded-xl border border-border bg-blue-50 px-4 py-3">
              <p className="text-[11px] uppercase tracking-[0.12em] text-blue-600">KPA Result</p>
              <p className="mt-1 text-lg font-bold text-blue-700">
                {finalWeightedResult.toFixed(2)}
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="space-y-4 p-4">
        {indicators.map((indicator) => (
          <KpiAssessmentItem
            key={indicator.kpiId}
            indicator={indicator}
            response={responses[indicator.kpiId] || { score: "", comment: "" }}
            onScoreChange={onScoreChange}
            onCommentChange={onCommentChange}
            onKeyIssuesChange={onKeyIssuesChange}
            onRecommendationChange={onRecommendationChange}
            isReadOnly={isReadOnly}
            reviewYear={reviewYear}
          />
        ))}
      </div>

      <div className="flex justify-end border-t border-border bg-muted/5 p-4">
        <Button type="button" onClick={onSave} disabled={saving || isReadOnly}>
          {saving ? <Icon name="Loader2" className="h-4 w-4 animate-spin" /> : <Icon name="Save" className="h-4 w-4" />}
          {saving ? "Saving..." : "Save KPA"}
        </Button>
      </div>
    </section>
  );
}

function OhlgsAssessmentPage({ requiredLevel = "region", locationType = "REGIONAL" }) {
  const { currentLocation } = useLocationContext();
  const { user } = useAuth();
  const currentYear = new Date().getFullYear();
  const defaultYear = YEAR_OPTIONS.find((option) => option.value === String(currentYear)) || YEAR_OPTIONS[YEAR_OPTIONS.length - 1];

  const [hasSelectedPeriod, setHasSelectedPeriod] = useState(false);
  const [selectedYear, setSelectedYear] = useState(defaultYear);
  const [selectedQuarter, setSelectedQuarter] = useState(QUARTER_OPTIONS[0]);
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [showAllItems, setShowAllItems] = useState(false);

  const [metadata, setMetadata] = useState(() => createInitialMetadata(user?.user?.fullName || ""));
  const [responses, setResponses] = useState({});
  const [generalComments, setGeneralComments] = useState("");
  const [recommendations, setRecommendations] = useState("");
  const [scopeOfAssessment, setScopeOfAssessment] = useState("");
  const [generalOutcome, setGeneralOutcome] = useState("");
  const [existingGeneralCommentsWriteUp, setExistingGeneralCommentsWriteUp] = useState(null);
  const [existingRecommendationsWriteUp, setExistingRecommendationsWriteUp] = useState(null);
  const [existingScopeOfAssessmentWriteUp, setExistingScopeOfAssessmentWriteUp] = useState(null);
  const [existingGeneralOutcomeWriteUp, setExistingGeneralOutcomeWriteUp] = useState(null);

  const [kpis, setKpis] = useState([]);
  const [kpas, setKpas] = useState([]);
  const [existingAssessments, setExistingAssessments] = useState({});
  const [assessmentLifecycle, setAssessmentLifecycle] = useState(null);
  const [savingLifecycle, setSavingLifecycle] = useState(false);
  const [pendingLifecycleStatus, setPendingLifecycleStatus] = useState(null);
  const [isLoadingData, setIsLoadingData] = useState(false);
  const [savingAssessment, setSavingAssessment] = useState(false);
  const [error, setError] = useState(null);

  const regionCode = currentLocation?.orgUnitId || "";
  const rCode =
    currentLocation?.level === "district"
      ? currentLocation?.regionOrgUnitId || currentLocation?.parentRegionOrgUnitId || ""
      : currentLocation?.level === "region"
        ? currentLocation?.orgUnitId || ""
        : "";
  const regionName = currentLocation?.name || "";
  
  const requiredLevelLabel = requiredLevel.charAt(0).toUpperCase() + requiredLevel.slice(1);
  const lifecycleType = getLifecycleTypeByLocationType(locationType);
  const assessmentStatus = String(assessmentLifecycle?.status || "").toUpperCase();
  const isAssessmentStarted = assessmentStatus === "STARTED";
  const isAssessmentCompleted = assessmentStatus === "COMPLETED";
  const isReadOnly = !isAssessmentStarted || isAssessmentCompleted;

  // Group KPIs by KPA
  const groupedByKpa = useMemo(() => {
    const grouped = new Map();
    
    kpis.forEach(kpi => {
      const kpaId = kpi.kpa?.kpaId;
      if (!grouped.has(kpaId)) {
        grouped.set(kpaId, {
          kpa: kpi.kpa || { kpaId: kpaId, kpaName: "Other", kpaDescription: "" },
          indicators: []
        });
      }
      grouped.get(kpaId).indicators.push(kpi);
    });
    
    return Array.from(grouped.values());
  }, [kpis]);

  // Calculate overall summary
  const overallSummary = useMemo(() => {
    let totalScore = 0;
    let maxScore = 0;
    let completedItems = 0;
    let totalItems = 0;

    Object.values(responses).forEach(response => {
      if (response.score !== "" && response.score !== null && response.score !== undefined) {
        totalScore += Number(response.score);
        completedItems += 1;
      }
    });

    kpis.forEach(kpi => {
      maxScore += kpi.maxScore || 4;
      totalItems += 1;
    });

    return { totalScore, maxScore, completedItems, totalItems };
  }, [responses, kpis]);

  const overallKpaResult = useMemo(() => {
    return groupedByKpa.reduce((total, group) => {
      const indicators = group?.indicators || [];
      const summedKpiResults = indicators.reduce((sum, indicator) => {
        const response = responses[indicator.kpiId] || {};
        const actualRating = Number(response?.score) || 0;
        const maxScore = Number(indicator?.maxScore) || 4;
        const kpiWeight = Number(indicator?.weight) || 1;
        if (actualRating > 0 && maxScore > 0) {
          return sum + (actualRating / maxScore) * kpiWeight;
        }
        return sum;
      }, 0);
      const kpaWeight = Number(group?.kpa?.weight) || 1;
      return total + (kpaWeight / 100) * summedKpiResults;
    }, 0);
  }, [groupedByKpa, responses]);

  // Fetch all data
  const fetchAllData = useCallback(async () => {
    if (!hasSelectedPeriod || !regionCode) return;

    setIsLoadingData(true);
    setError(null);

    try {
      const year = selectedYear.value;
      
      // Fetch KPIs
      const kpiResponse = await getKPIsByYearAndLocationType(year, locationType);
      const kpiData = kpiResponse?.data || kpiResponse || [];
      setKpis(kpiData);
      
      // Fetch KPAs
      const kpaResponse = await getOhlgsKpas();
      const kpaData = kpaResponse?.data || kpaResponse || [];
      setKpas(kpaData);
      
      // Fetch existing assessments
      const assessmentResponse = await getContractPerformanceAssessments({
        locationCode: regionCode,
        year: year,
      });
      const assessmentData = filterAssessmentsForScope(assessmentResponse?.data || assessmentResponse || [], {
        locationCode: regionCode,
        year,
        locationType,
      });
      const assessmentMap = {};
      assessmentData.forEach((assessment) => {
        const kpiId = getAssessmentKpiId(assessment);
        if (kpiId) {
          assessmentMap[kpiId] = assessment;
        }
      });
      setExistingAssessments(assessmentMap);
      
      // Load existing responses into state
      const existingResponses = {};
      kpiData.forEach(kpi => {
        const existing = assessmentMap[kpi.kpiId];
        if (existing) {
          existingResponses[kpi.kpiId] = {
            score: existing.actualScore,
            comment: existing.observation || "",
            keyIssues: existing.keyIssues || "",
            recommendation: existing.recommendation || "",
          };
        } else {
          existingResponses[kpi.kpiId] = { score: "", comment: "", keyIssues: "", recommendation: "" };
        }
      });
      setResponses(existingResponses);

      const writeUpsResponse = await getWriteUps();
      const writeUpRows = (writeUpsResponse?.data || writeUpsResponse || [])
        .filter((writeUp) => (
          normalizeLocationCode(writeUp?.locationCode) === normalizeLocationCode(regionCode) &&
          String(writeUp?.year || "") === String(year) &&
          String(writeUp?.type || "") === String(lifecycleType)
        ))
        .sort((a, b) => {
          const aStamp = writeUpTimestamp(a?.updatedAt || a?.createdAt);
          const bStamp = writeUpTimestamp(b?.updatedAt || b?.createdAt);
          return bStamp - aStamp;
        });
      const generalWriteUp = writeUpRows.find((writeUp) => writeUp.sectionCommented === OHLGS_GENERAL_COMMENTS_SECTION) || null;
      const recommendationsWriteUp = writeUpRows.find((writeUp) => writeUp.sectionCommented === OHLGS_RECOMMENDATIONS_SECTION) || null;
      const scopeOfAssessmentWriteUp = writeUpRows.find((writeUp) => writeUp.sectionCommented === OHLGS_SCOPE_OF_ASSESSMENT_SECTION) || null;
      const generalOutcomeWriteUp = writeUpRows.find((writeUp) => writeUp.sectionCommented === OHLGS_GENERAL_OUTCOME_SECTION) || null;
      setExistingGeneralCommentsWriteUp(generalWriteUp);
      setExistingRecommendationsWriteUp(recommendationsWriteUp);
      setExistingScopeOfAssessmentWriteUp(scopeOfAssessmentWriteUp);
      setExistingGeneralOutcomeWriteUp(generalOutcomeWriteUp);
      setGeneralComments(generalWriteUp?.comments || "");
      setRecommendations(recommendationsWriteUp?.comments || "");
      setScopeOfAssessment(scopeOfAssessmentWriteUp?.comments || "");
      setGeneralOutcome(generalOutcomeWriteUp?.comments || "");

      const lifecycleResponse = await getAssessments({
        type: lifecycleType,
        year,
        districtId: regionCode,
      });
      const lifecycleRows = lifecycleResponse?.data || lifecycleResponse || [];
      const matchedLifecycle = (Array.isArray(lifecycleRows) ? lifecycleRows : []).find((row) => (
        String(row?.type || "").toUpperCase() === lifecycleType &&
        String(row?.year || "") === String(year) &&
        normalizeLocationCode(row?.districtId) === normalizeLocationCode(regionCode)
      )) || null;
      setAssessmentLifecycle(matchedLifecycle);
      
    } catch (err) {
      console.error("Error fetching data:", err);
      const statusCode = err?.response?.status;
      if (statusCode === 404) {
        setError(null);
        setKpis([]);
        setKpas([]);
        setResponses({});
        setExistingAssessments({});
        setAssessmentLifecycle(null);
        setExistingGeneralCommentsWriteUp(null);
        setExistingRecommendationsWriteUp(null);
        setExistingScopeOfAssessmentWriteUp(null);
        setExistingGeneralOutcomeWriteUp(null);
        setGeneralComments("");
        setRecommendations("");
        setScopeOfAssessment("");
        setGeneralOutcome("");
      } else {
        setError(err);
        toast.error("Failed to load assessment data");
      }
    } finally {
      setIsLoadingData(false);
    }
  }, [hasSelectedPeriod, regionCode, selectedYear, locationType, lifecycleType]);

  useEffect(() => {
    if (hasSelectedPeriod && regionCode) {
      fetchAllData();
    }
  }, [hasSelectedPeriod, regionCode, fetchAllData]);

  const updateMetadata = (field) => (event) => {
    setMetadata((prev) => ({ ...prev, [field]: event.target.value }));
  };

  const updateScore = (kpiId, value) => {
    setResponses((current) => ({
      ...current,
      [kpiId]: {
        ...current[kpiId],
        score: value === "" ? "" : Number(value),
      },
    }));
  };

  const updateComment = (kpiId, value) => {
    setResponses((current) => ({
      ...current,
      [kpiId]: {
        ...current[kpiId],
        comment: value,
      },
    }));
  };

  const updateKeyIssues = (kpiId, value) => {
    setResponses((current) => ({
      ...current,
      [kpiId]: {
        ...current[kpiId],
        keyIssues: value,
      },
    }));
  };

  const updateRecommendation = (kpiId, value) => {
    setResponses((current) => ({
      ...current,
      [kpiId]: {
        ...current[kpiId],
        recommendation: value,
      },
    }));
  };

  const saveCurrentResponses = async () => {
    setSavingAssessment(true);
    let savedCount = 0;
    const savePromises = [];
    const currentUsername = user?.user?.username || "system";
    const currentFullName = user?.user?.fullName || currentUsername;
    const normalizedRole = user?.user?.role || requiredLevel;
    const year = selectedYear.value;

    Object.entries(responses).forEach(([kpiId, response]) => {
      if (response.score !== undefined && response.score !== null && response.score !== "") {
        const existing = existingAssessments[parseInt(kpiId)];
        const payload = {
          contractPerformanceAssessmentId: existing?.contractPerformanceAssessmentId || 0,
          kpiId: parseInt(kpiId),
          parentId: rCode,
          locationCode: regionCode,
          observation: response.comment || "",
          keyIssues: response.keyIssues || "",
          recommendation: response.recommendation || "",
          actualScore: parseFloat(response.score),
          status: "APPROVED",
          locationType: locationType,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
          createdBy: currentUsername,
          updatedBy: currentUsername,
        };
        
        savePromises.push(saveContractPerformanceAssessment(payload));
      }
    });

    try {
      if (savePromises.length > 0) {
        const results = await Promise.all(savePromises);
        savedCount = results.length;
      }

      const writeUpPayloads = [
        {
          writeUpId: existingGeneralCommentsWriteUp?.writeUpId || 0,
          username: currentUsername,
          fullName: currentFullName,
          userRole: normalizedRole,
          type: lifecycleType,
          title: "OHLGS General Comments",
          locationCode: regionCode,
          year: String(year),
          sectionCommented: OHLGS_GENERAL_COMMENTS_SECTION,
          comments: generalComments || "",
          createdBy: currentUsername,
        },
        {
          writeUpId: existingRecommendationsWriteUp?.writeUpId || 0,
          username: currentUsername,
          fullName: currentFullName,
          userRole: normalizedRole,
          type: lifecycleType,
          title: "OHLGS Recommendations",
          locationCode: regionCode,
          year: String(year),
          sectionCommented: OHLGS_RECOMMENDATIONS_SECTION,
          comments: recommendations || "",
          createdBy: currentUsername,
        },
        {
          writeUpId: existingScopeOfAssessmentWriteUp?.writeUpId || 0,
          username: currentUsername,
          fullName: currentFullName,
          userRole: normalizedRole,
          type: lifecycleType,
          title: "OHLGS Scope of Assessment",
          locationCode: regionCode,
          year: String(year),
          sectionCommented: OHLGS_SCOPE_OF_ASSESSMENT_SECTION,
          comments: scopeOfAssessment || "",
          createdBy: currentUsername,
        },
        {
          writeUpId: existingGeneralOutcomeWriteUp?.writeUpId || 0,
          username: currentUsername,
          fullName: currentFullName,
          userRole: normalizedRole,
          type: lifecycleType,
          title: "OHLGS General Outcome",
          locationCode: regionCode,
          year: String(year),
          sectionCommented: OHLGS_GENERAL_OUTCOME_SECTION,
          comments: generalOutcome || "",
          createdBy: currentUsername,
        },
      ];
      const writeUpResults = await Promise.all(writeUpPayloads.map((payload) => saveWriteUp(payload)));
      setExistingGeneralCommentsWriteUp(writeUpResults[0]?.data || writeUpResults[0] || existingGeneralCommentsWriteUp);
      setExistingRecommendationsWriteUp(writeUpResults[1]?.data || writeUpResults[1] || existingRecommendationsWriteUp);
      setExistingScopeOfAssessmentWriteUp(writeUpResults[2]?.data || writeUpResults[2] || existingScopeOfAssessmentWriteUp);
      setExistingGeneralOutcomeWriteUp(writeUpResults[3]?.data || writeUpResults[3] || existingGeneralOutcomeWriteUp);

      if (savePromises.length > 0) {
        toast.success(`${savedCount} assessment(s) saved successfully`);
      } else {
        toast.success("Comments and recommendations saved successfully");
      }

      // Refresh existing assessments
      if (savePromises.length > 0) {
        const assessmentResponse = await getContractPerformanceAssessments({
          locationCode: regionCode,
          year,
        });
        const assessmentData = filterAssessmentsForScope(assessmentResponse?.data || assessmentResponse || [], {
          locationCode: regionCode,
          year,
          locationType,
        });
        const assessmentMap = {};
        assessmentData.forEach((assessment) => {
          const kpiId = getAssessmentKpiId(assessment);
          if (kpiId) {
            assessmentMap[kpiId] = assessment;
          }
        });
        setExistingAssessments(assessmentMap);
      }
    } catch (err) {
      console.error("Error saving assessments/write-ups:", err);
      toast.error("Failed to save assessments or write-ups");
    }
    
    setSavingAssessment(false);
  };

  const openLifecycleConfirmation = (nextStatus) => {
    setPendingLifecycleStatus(nextStatus);
  };

  const closeLifecycleConfirmation = () => {
    if (savingLifecycle) return;
    setPendingLifecycleStatus(null);
  };

  const handleLifecycleStatusChange = async () => {
    if (!pendingLifecycleStatus) return;
    try {
      setSavingLifecycle(true);
      const currentUsername = user?.user?.username || "system";
      const currentFullName = user?.user?.fullName || currentUsername;
      const normalizedRole = user?.user?.role || requiredLevel;
      const nowDate = formatDateInputValue(new Date());
      const payload = {
        id: assessmentLifecycle?.id || 0,
        username: currentUsername,
        fullName: currentFullName,
        userRole: normalizedRole,
        type: lifecycleType,
        districtId: regionCode,
        year: String(selectedYear?.value || ""),
        status: pendingLifecycleStatus,
        assessmentStartDate: assessmentLifecycle?.assessmentStartDate || nowDate,
      };

      const response = await saveAssessmentLifecycle(payload);
      const lifecycleData = response?.data || response || null;
      setAssessmentLifecycle(lifecycleData);
      toast.success(pendingLifecycleStatus === "STARTED" ? "Assessment started" : "Assessment completed");
    } catch (err) {
      console.error("Error updating assessment lifecycle:", err);
      toast.error("Failed to update assessment status");
    } finally {
      setSavingLifecycle(false);
      setPendingLifecycleStatus(null);
    }
  };

  const handleYearChange = (option) => {
    setSelectedYear(option);
    setHasSelectedPeriod(false);
    setKpis([]);
    setResponses({});
    setGeneralComments("");
    setRecommendations("");
    setScopeOfAssessment("");
    setGeneralOutcome("");
    setExistingGeneralCommentsWriteUp(null);
    setExistingRecommendationsWriteUp(null);
    setExistingScopeOfAssessmentWriteUp(null);
    setExistingGeneralOutcomeWriteUp(null);
    setAssessmentLifecycle(null);
  };

  const handleQuarterChange = (option) => {
    setSelectedQuarter(option);
    setHasSelectedPeriod(false);
    setKpis([]);
    setResponses({});
    setGeneralComments("");
    setRecommendations("");
    setScopeOfAssessment("");
    setGeneralOutcome("");
    setExistingGeneralCommentsWriteUp(null);
    setExistingRecommendationsWriteUp(null);
    setExistingScopeOfAssessmentWriteUp(null);
    setExistingGeneralOutcomeWriteUp(null);
    setAssessmentLifecycle(null);
  };

  const handleLoadAssessment = () => {
    if (selectedYear && selectedQuarter) {
      setHasSelectedPeriod(true);
    }
  };

  const isLoading = isLoadingData;

  if (currentLocation?.level !== requiredLevel) {
    return <SelectionRequiredState requiredLevelLabel={requiredLevelLabel} requiredLevelName={requiredLevel} />;
  }

  if (!hasSelectedPeriod) {
    return (
      <AssessmentPeriodSelection
        selectedYear={selectedYear}
        selectedQuarter={selectedQuarter}
        onYearChange={handleYearChange}
        onQuarterChange={handleQuarterChange}
        onLoad={handleLoadAssessment}
        isLoading={isLoading}
      />
    );
  }

  if (isLoading) {
    return (
      <div className="main-content">
        <div className="card flex items-center justify-center py-12">
          <div className="text-center">
            <div className="mb-4 h-8 w-8 animate-spin rounded-full border-4 border-[#16325a] border-t-transparent mx-auto" />
            <p className="text-muted-foreground">Loading OHLGS assessment data for {selectedYear?.value} ...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="main-content">
        <div className="card border-red-200 bg-red-50">
          <h2 className="mb-2 text-xl font-semibold text-red-700">Error Loading Data</h2>
          <p className="text-red-600">{error?.message || error?.response?.data?.message || "An error occurred while loading data"}</p>
          <div className="flex gap-2 mt-4">
            <Button variant="outline" onClick={() => setHasSelectedPeriod(false)}>Back to Selection</Button>
            <Button variant="outline" onClick={fetchAllData}>Retry</Button>
          </div>
        </div>
      </div>
    );
  }

  if (kpis.length === 0) {
    return (
      <div className="main-content">
        <div className="card border-emerald-200 bg-emerald-50">
          <h2 className="mb-2 text-xl font-semibold text-emerald-800">No KPA Found</h2>
          <p className="text-emerald-700">
            No KPA was found for year {selectedYear?.value} and {regionName || "the selected location"}.
          </p>
          <div className="mt-4">
            <Button variant="outline" onClick={() => setHasSelectedPeriod(false)}>Back to Selection</Button>
          </div>
        </div>
      </div>
    );
  }

  const currentKpaGroup = groupedByKpa[currentStepIndex];
  const isLastStep = currentStepIndex === groupedByKpa.length - 1;
  const showSummarySection = showAllItems || isLastStep;
  const lifecycleConfirmTitle = pendingLifecycleStatus === "STARTED" ? "Start OHLGS Report" : "Complete OHLGS Report";
  const lifecycleConfirmMessage = pendingLifecycleStatus === "STARTED"
    ? "Warning: starting this report will unlock editing for this period."
    : "Warning: completing this report will lock editing for this period.";
  const lifecycleConfirmActionLabel = pendingLifecycleStatus === "STARTED" ? "Start Assessment" : "Complete Assessment";

  return (
    <div className="min-h-full bg-background">
      <div className="main-content space-y-5 pb-6">
        {/* Header Section */}
        <section className="card p-4 md:p-5">
          <div className="space-y-3">
            <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
              <div className="min-w-0">
                <p className="text-xs uppercase tracking-[0.12em] text-muted-foreground">OHLGS Assessment</p>
                <h2 className="mt-1 text-lg font-semibold text-foreground">Office of the Head of the Local Government Service</h2>
                <p className="mt-1 text-sm text-muted-foreground">
                  {regionName} {selectedYear?.value} 
                </p>
              </div>
              <div className="flex flex-wrap gap-2">
                <Button
                  type="button"
                  size="sm"
                  onClick={() => openLifecycleConfirmation(isAssessmentStarted ? "COMPLETED" : "STARTED")}
                  disabled={savingLifecycle || isAssessmentCompleted}
                  className="bg-green-600 hover:bg-green-700 text-white disabled:opacity-60"
                >
                  {savingLifecycle ? (
                    <>
                      <Icon name="Loader2" className="h-4 w-4 animate-spin" />
                      Updating...
                    </>
                  ) : isAssessmentCompleted ? (
                    <>
                      <Icon name="CheckCircle2" className="h-4 w-4" />
                      Assessment Completed
                    </>
                  ) : isAssessmentStarted ? (
                    <>
                      <Icon name="CheckSquare" className="h-4 w-4" />
                      Complete Assessment
                    </>
                  ) : (
                    <>
                      <Icon name="PlayCircle" className="h-4 w-4" />
                      Start Assessment
                    </>
                  )}
                </Button>
                <Button type="button" size="sm" variant="outline" onClick={() => setShowAllItems((prev) => !prev)}>
                  <Icon name="List" className="h-4 w-4" />
                  {showAllItems ? "Back to Step View" : "Show All KPAs"}
                </Button>
                <Button type="button" size="sm" variant="ghost" onClick={() => setHasSelectedPeriod(false)}>
                  <Icon name="RefreshCw" className="h-4 w-4" /> Change Period
                </Button>
              </div>
            </div>

            <div className="grid gap-3 xl:grid-cols-[minmax(0,1fr)_360px] xl:items-end">
              <div className="flex flex-wrap items-center gap-2 text-xs">
                <span className="inline-flex items-center gap-1 rounded-full bg-muted px-2.5 py-1 text-muted-foreground">
                  <Icon name="MapPin" className="h-3.5 w-3.5" /> {regionName}
                </span>
                <span className="inline-flex items-center gap-1 rounded-full bg-muted px-2.5 py-1 text-muted-foreground">
                  <Icon name="Hash" className="h-3.5 w-3.5" /> {regionCode}
                </span>
                <span className="inline-flex items-center gap-1 rounded-full bg-[#16325a]/10 px-2.5 py-1 text-[#16325a]">
                  <Icon name="Calendar" className="h-3.5 w-3.5" /> {selectedYear?.value}
                </span>
                <span className="inline-flex items-center gap-1 rounded-full bg-muted px-2.5 py-1 text-muted-foreground">
                  <Icon name="CheckSquare" className="h-3.5 w-3.5" /> {overallSummary.completedItems}/{overallSummary.totalItems} complete
                </span>
                <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 ${
                  isAssessmentCompleted
                    ? "bg-emerald-100 text-emerald-700"
                    : isAssessmentStarted
                      ? "bg-blue-100 text-blue-700"
                      : "bg-amber-100 text-amber-700"
                }`}>
                  <Icon name={isAssessmentCompleted ? "Lock" : isAssessmentStarted ? "PenLine" : "AlertCircle"} className="h-3.5 w-3.5" />
                  {isAssessmentCompleted ? "Completed (Read Only)" : isAssessmentStarted ? "Started (Editable)" : "Not Started (Read Only)"}
                </span>
              </div>
            </div>
          </div>
        </section>

        {/* Metadata Section */}
        <section className="card p-4 md:p-5">
          <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
            <label className="space-y-2">
              <span className="text-xs font-semibold uppercase tracking-[0.12em] text-muted-foreground">
                Lead Assessor
              </span>
              <input
                value={metadata.leadAssessor}
                onChange={updateMetadata("leadAssessor")}
                disabled={isReadOnly}
                className="w-full rounded-xl border border-input bg-background px-3 py-2.5 text-sm text-foreground outline-none transition focus:border-[#16325a] focus:ring-2 focus:ring-[#16325a]/20"
                placeholder="Enter lead assessor"
              />
            </label>
            <div className="rounded-xl border border-border bg-muted/20 px-4 py-3">
              <p className="text-[11px] uppercase tracking-[0.12em] text-muted-foreground">Date</p>
              <p className="mt-1 text-sm font-semibold text-foreground">{metadata.assessmentDate || "-"}</p>
            </div>
            <div className="rounded-xl border border-border bg-muted/20 px-4 py-3">
              <p className="text-[11px] uppercase tracking-[0.12em] text-muted-foreground">Start Time</p>
              <p className="mt-1 text-sm font-semibold text-foreground">{metadata.startTime || "-"}</p>
            </div>
            <div className="rounded-xl border border-border bg-muted/20 px-4 py-3">
              <p className="text-[11px] uppercase tracking-[0.12em] text-muted-foreground">End Time</p>
              <p className="mt-1 text-sm font-semibold text-foreground">{metadata.endTime || "Pending save"}</p>
            </div>
          </div>

          <div className="mt-4 grid gap-3 md:grid-cols-3">
            <div className="rounded-xl border border-border bg-background px-4 py-3">
              <p className="text-[11px] uppercase tracking-[0.12em] text-muted-foreground">Overall Score</p>
              <p className="mt-1 text-lg font-semibold text-foreground">
              {overallKpaResult.toFixed(2)} 
                <span className="ml-1 text-sm font-medium text-muted-foreground">/100 </span>
              </p>
             
            </div>
            <div className="rounded-xl border border-border bg-background px-4 py-3">
              <p className="text-[11px] uppercase tracking-[0.12em] text-muted-foreground">Scored KPIs</p>
              <p className="mt-1 text-lg font-semibold text-foreground">
                {overallSummary.completedItems}
                <span className="ml-1 text-sm font-medium text-muted-foreground">/ {overallSummary.totalItems}</span>
              </p>
            </div>
            <div className="rounded-xl border border-border bg-background px-4 py-3">
              <p className="text-[11px] uppercase tracking-[0.12em] text-muted-foreground">Location</p>
              <p className="mt-1 text-sm font-semibold text-foreground">{regionName || "No Location Selected"}</p>
            </div>
          </div>
        </section>

        {/* KPA Assessment Cards */}
        <div className="space-y-5">
          {showAllItems ? (
            groupedByKpa.map((group, index) => (
              <KpaAssessmentCard
                key={group.kpa.kpaId}
                kpa={group.kpa}
                indicators={group.indicators}
                responses={responses}
                onScoreChange={updateScore}
                onCommentChange={updateComment}
                onKeyIssuesChange={updateKeyIssues}
                onRecommendationChange={updateRecommendation}
                onSave={saveCurrentResponses}
                saving={savingAssessment}
                isReadOnly={isReadOnly}
                reviewYear={selectedYear?.value || ""}
              />
            ))
          ) : currentKpaGroup ? (
            <KpaAssessmentCard
              key={currentKpaGroup.kpa.kpaId}
              kpa={currentKpaGroup.kpa}
              indicators={currentKpaGroup.indicators}
              responses={responses}
              onScoreChange={updateScore}
              onCommentChange={updateComment}
              onKeyIssuesChange={updateKeyIssues}
              onRecommendationChange={updateRecommendation}
              onSave={saveCurrentResponses}
              saving={savingAssessment}
              isReadOnly={isReadOnly}
              reviewYear={selectedYear?.value || ""}
            />
          ) : null}

          {!showAllItems && groupedByKpa.length > 0 && (
            <section className="card p-4 md:p-5">
              <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setCurrentStepIndex((prev) => Math.max(prev - 1, 0))}
                  disabled={currentStepIndex === 0}
                >
                  <Icon name="ChevronLeft" className="h-4 w-4" /> Previous
                </Button>
                <div className="text-center">
                  <p className="text-xs uppercase tracking-[0.14em] text-muted-foreground">Assessment Navigation</p>
                  <p className="mt-1 text-sm text-foreground">
                    KPA {currentStepIndex + 1} of {groupedByKpa.length}
                  </p>
                </div>
                <Button
                  type="button"
                  onClick={() => setCurrentStepIndex((prev) => Math.min(prev + 1, groupedByKpa.length - 1))}
                  disabled={currentStepIndex === groupedByKpa.length - 1}
                >
                  Next <Icon name="ChevronRight" className="h-4 w-4" />
                </Button>
              </div>
            </section>
          )}
        </div>

        {/* Summary Section */}
        {showSummarySection && (
          <section className="card overflow-hidden p-0">
            <div className="border-b border-border px-5 py-4">
              <div className="flex flex-col gap-2">
                <p className="text-[11px] font-medium uppercase tracking-[0.14em] text-muted-foreground">
                  Assessment Notes
                </p>
                <h4 className="text-lg font-semibold text-foreground">General Comments and Recommendations</h4>
              </div>
            </div>

            <div className="grid gap-6 p-5 lg:grid-cols-2">
              <label className="space-y-2">
                <span className="text-xs font-semibold uppercase tracking-[0.12em] text-muted-foreground">
                  General Comments / Observations
                </span>
                <textarea
                  rows={10}
                  value={generalComments}
                  onChange={(e) => setGeneralComments(e.target.value)}
                  disabled={isReadOnly}
                  placeholder="Provide overall observations about the OHLGS assessment..."
                  className="w-full rounded-2xl border border-input bg-background px-3 py-3 text-sm leading-6 text-foreground outline-none transition focus:border-[#16325a] focus:ring-2 focus:ring-[#16325a]/20"
                />
              </label>

              <label className="space-y-2">
                <span className="text-xs font-semibold uppercase tracking-[0.12em] text-muted-foreground">
                  Recommendations
                </span>
                <textarea
                  rows={10}
                  value={recommendations}
                  onChange={(e) => setRecommendations(e.target.value)}
                  disabled={isReadOnly}
                  placeholder="Provide actionable recommendations based on the assessment findings..."
                  className="w-full rounded-2xl border border-input bg-background px-3 py-3 text-sm leading-6 text-foreground outline-none transition focus:border-[#16325a] focus:ring-2 focus:ring-[#16325a]/20"
                />
              </label>

              <label className="space-y-2">
                <span className="text-xs font-semibold uppercase tracking-[0.12em] text-muted-foreground">
                  Scope of Assessment
                </span>
                <textarea
                  rows={10}
                  value={scopeOfAssessment}
                  onChange={(e) => setScopeOfAssessment(e.target.value)}
                  disabled={isReadOnly}
                  placeholder="Provide scope of the assessment for this region..."
                  className="w-full rounded-2xl border border-input bg-background px-3 py-3 text-sm leading-6 text-foreground outline-none transition focus:border-[#16325a] focus:ring-2 focus:ring-[#16325a]/20"
                />
              </label>

              <label className="space-y-2">
                <span className="text-xs font-semibold uppercase tracking-[0.12em] text-muted-foreground">
                  General Outcome
                </span>
                <textarea
                  rows={10}
                  value={generalOutcome}
                  onChange={(e) => setGeneralOutcome(e.target.value)}
                  disabled={isReadOnly}
                  placeholder="Provide general outcome for this region..."
                  className="w-full rounded-2xl border border-input bg-background px-3 py-3 text-sm leading-6 text-foreground outline-none transition focus:border-[#16325a] focus:ring-2 focus:ring-[#16325a]/20"
                />
              </label>
            </div>

            <div className="flex justify-end border-t border-border px-5 py-4">
              <Button 
                type="button" 
                onClick={saveCurrentResponses} 
                disabled={savingAssessment || isReadOnly}
                className="bg-[#16325a] hover:bg-[#0f243f]"
              >
                <Icon name="Save" className="h-4 w-4" />
                {savingAssessment ? "Saving All..." : "Save All Assessments"}
              </Button>
            </div>
          </section>
        )}

        {/* Footer */}
        <div className="text-center text-xs text-muted-foreground py-4">
          <Icon name="Database" className="inline h-3 w-3 mr-1" />
          Data reflects OHLGS assessments for {selectedYear?.value} 
        </div>
      </div>

      {pendingLifecycleStatus ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-md rounded-2xl border border-border bg-background p-5 shadow-xl">
            <div className="flex items-start gap-3">
              <div className="mt-0.5 rounded-full bg-amber-100 p-2 text-amber-700">
                <Icon name="AlertTriangle" className="h-4 w-4" />
              </div>
              <div>
                <h3 className="text-base font-semibold text-foreground">{lifecycleConfirmTitle}</h3>
                <p className="mt-1 text-sm text-muted-foreground">{lifecycleConfirmMessage}</p>
              </div>
            </div>
            <div className="mt-5 flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={closeLifecycleConfirmation} disabled={savingLifecycle}>
                Cancel
              </Button>
              <Button
                type="button"
                className="bg-green-600 hover:bg-green-700 text-white"
                onClick={handleLifecycleStatusChange}
                disabled={savingLifecycle}
              >
                {savingLifecycle ? (
                  <>
                    <Icon name="Loader2" className="h-4 w-4 animate-spin" />
                    Updating...
                  </>
                ) : (
                  lifecycleConfirmActionLabel
                )}
              </Button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

export function OhlgsRegionalPage() {
  return <OhlgsAssessmentPage requiredLevel="region" locationType="REGIONAL" />;
}

export function OhlgsDistrictPage() {
  return <OhlgsAssessmentPage requiredLevel="district" locationType="DISTRICT" />;
}

export default OhlgsRegionalPage;